import { Friend, FriendRequest, FriendshipStatusResponse, SuggestedFriend } from '@/types/friendship';
import {
    FriendRequestWire,
    FriendshipStatusWire,
    FriendshipWire,
    SuggestedFriendWire,
} from '../schemas/friendship';
import { mapUserWireToDomain } from './user';

/**
 * Mapping functions to convert wire format (backend) to domain format (app)
 */

/**
 * Convert wire friend request format to domain friend request format
 * Handles type conversions: ISO strings -> Unix timestamps, null -> undefined
 */
export function mapFriendRequestWireToDomain(wire: FriendRequestWire): FriendRequest {
  // Convert ISO datetime strings to Unix timestamps (milliseconds)
  const createdAt = wire.created_at ? new Date(wire.created_at).getTime() : Date.now();
  const updatedAt = wire.updated_at ? new Date(wire.updated_at).getTime() : Date.now();

  return {
    id: wire.id,
    requesterId: wire.requester_id,
    receiverId: wire.receiver_id,
    status: wire.status,
    createdAt,
    updatedAt,
    // Map joined user data if present
    requester: wire.requester ? mapUserWireToDomain(wire.requester) : undefined,
    receiver: wire.receiver ? mapUserWireToDomain(wire.receiver) : undefined,
  };
}

/**
 * Convert wire friendship format to domain friendship format
 * Handles type conversions: ISO strings -> Unix timestamps
 *
 * Note: Backend should always include friend user data when fetching friendships.
 * If friend data is missing, this will throw an error as it's required for the domain model.
 */
export function mapFriendshipWireToDomain(wire: FriendshipWire): Friend {
  // Convert ISO datetime string to Unix timestamp (milliseconds)
  const createdAt = wire.created_at ? new Date(wire.created_at).getTime() : Date.now();

  if (!wire.friend) {
    throw new Error(
      `Friend data missing for friendship ${wire.id}. Backend should include friend user data when fetching friendships.`
    );
  }

  return {
    id: wire.id,
    friendId: wire.friend_id,
    createdAt,
    // Map joined friend user data (required for Friend model)
    user: mapUserWireToDomain(wire.friend),
    // Map optional enrichment stats
    friendCount: wire.friend_count,
    mutualFriendCount: wire.mutual_friend_count,
    checkInCount: wire.check_in_count,
  };
}

/**
 * Convert wire suggested friend format to domain suggested friend format
 */
export function mapSuggestedFriendWireToDomain(wire: SuggestedFriendWire): SuggestedFriend {
  return {
    userId: wire.user_id,
    username: wire.username || undefined,
    displayUsername: wire.display_username || undefined,
    name: wire.name || '',
    image: wire.image || undefined,
    bio: wire.bio || undefined,
    mutualFriendCount: wire.mutual_friend_count,
  };
}

/**
 * Convert wire friendship status format to domain friendship status format
 */
export function mapFriendshipStatusWireToDomain(
  wire: FriendshipStatusWire
): FriendshipStatusResponse {
  return {
    status: wire.status,
    requestId: wire.request_id || undefined, // Convert null to undefined
  };
}
